import { useState } from "react";

export const useTemplateForm = () => {
  const [templateForm, setTemplateForm] = useState({
    name: "",
    body: "",
    category: "UTILITY",
    variables: []
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Validation functions
  const validateTemplateName = (name) => {
    const pattern = /^[a-z0-9_]+$/;
    return pattern.test(name);
  };

  const extractVariablesFromBody = (body) => {
    const regex = /\{\{([^}]+)\}\}/g;
    const variables = [];
    let match;
    while ((match = regex.exec(body)) !== null) {
      variables.push(match[1].trim());
    }
    return [...new Set(variables)]; // Remove duplicates
  };

  const validateForm = () => {
    const errors = {};
    
    // Validate template name
    if (!templateForm.name.trim()) {
      errors.name = "Template name is required";
    } else if (!validateTemplateName(templateForm.name)) {
      errors.name = "Template name can only contain lowercase letters, numbers, and underscores";
    }
    
    // Validate template body
    if (!templateForm.body.trim()) {
      errors.body = "Template body is required";
    }
    
    // Validate category
    if (!templateForm.category) {
      errors.category = "Category is required";
    }
    
    // Validate variables (if any exist)
    const filteredVariables = templateForm.variables.filter(v => v.name.trim() !== "");
    if (filteredVariables.length > 0) {
      // Check if all variables have sample values
      const missingValues = filteredVariables.some(v => !v.sampleValue.trim());
      if (missingValues) {
        errors.variables = "All variables must have sample values";
      }
    }
    
    // Validate variable usage in template body
    if (templateForm.body.trim()) {
      const usedVariables = extractVariablesFromBody(templateForm.body);
      const definedVariables = filteredVariables.map(v => v.name.trim());
      
      // Check if all used variables are defined
      const undefinedVariables = usedVariables.filter(v => !definedVariables.includes(v));
      if (undefinedVariables.length > 0) {
        errors.body = `Undefined variables used in template: ${undefinedVariables.join(", ")}`;
      }
      
      // Check if all defined variables are used (only if there are defined variables)
      if (definedVariables.length > 0) {
        const unusedVariables = definedVariables.filter(v => !usedVariables.includes(v));
        if (unusedVariables.length > 0) {
          errors.variables = `Unused variables defined: ${unusedVariables.join(", ")}`;
        }
      }
    }
    
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setTemplateForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  const handleVariableChange = (index, field, value) => {
    const updatedVariables = [...templateForm.variables];
    updatedVariables[index] = {
      ...updatedVariables[index],
      [field]: value
    };
    
    setTemplateForm(prev => ({
      ...prev,
      variables: updatedVariables
    }));
    
    // Clear validation error for variables
    if (validationErrors.variables) {
      setValidationErrors(prev => ({
        ...prev,
        variables: undefined
      }));
    }
  };
  
  const addVariable = () => {
    setTemplateForm(prev => ({
      ...prev,
      variables: [...prev.variables, { name: "", sampleValue: "" }]
    }));
  };
  
  const removeVariable = (index) => {
    const updatedVariables = [...templateForm.variables];
    updatedVariables.splice(index, 1);
    
    setTemplateForm(prev => ({
      ...prev,
      variables: updatedVariables
    }));
  };

  const resetForm = () => {
    setTemplateForm({
      name: "",
      body: "",
      category: "UTILITY",
      variables: []
    });
    setValidationErrors({});
    setFormSubmitting(false);
  };

  const loadTemplateData = (template) => {
    // Extract variables from template fields
    const variables = template.fields.map(field => ({
      name: field,
      sampleValue: ""
    }));
    
    setTemplateForm({
      name: template.name,
      body: template.value,
      category: template.metadata?.category || "UTILITY",
      variables: variables
    });
    setValidationErrors({});
  };

  const getFormData = () => {
    // Filter out empty variables
    const filteredVariables = templateForm.variables.filter(v => v.name.trim() !== "");
    
    return {
      name: templateForm.name,
      body: templateForm.body,
      category: templateForm.category,
      language: "en", // Hardcoded as requested
      variables: filteredVariables
    };
  };

  return {
    templateForm,
    validationErrors,
    formSubmitting,
    setFormSubmitting,
    validateForm,
    handleFormChange,
    handleVariableChange,
    addVariable,
    removeVariable,
    resetForm,
    loadTemplateData,
    getFormData
  };
}; 